import { Button, Checkbox, Classes, FormGroup, InputGroup, MenuItem, TextArea } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";
import { useField } from "formik";
import React, { useState } from "react";
import { v4 as uuid } from "uuid";
import { DatePicker } from "@blueprintjs/datetime";
import { Popover2 } from "@blueprintjs/popover2";
import { ProjectVariable } from "types";
import { getFormikFieldValueReference } from "utils/utils";

type Props = {
    variable: ProjectVariable;
};

export function StringField({ variable }: Props) {
    const { name, label, required } = variable;
    const [field, , helpers] = useField<string>(getFormikFieldValueReference(name));
    return (
        <FormGroup
            key={`formstring_${name}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
        >
            <InputGroup
                key={`inputstring_${name}`}
                value={field.value}
                onChange={(e) => helpers.setValue(e.target.value)}
            />
        </FormGroup>
    );
}

export function MaskedStringField({ variable }: Props) {
    const { name, label, required } = variable;
    const [field, , helpers] = useField<string>(getFormikFieldValueReference(name));
    const [show, setShow] = useState(false);
    return (
        <FormGroup key={`formmaskedstring_${name}`} label={label} labelInfo={required ? "*" : ""} labelFor="text-input">
            <InputGroup
                key={`maskedstring_${name}`}
                value={field.value}
                onChange={(e) => helpers.setValue(e.target.value)}
                type={show ? "text" : "password"}
            />
            <Checkbox checked={show} label="Show" onChange={() => setShow(!show)} />
        </FormGroup>
    );
}

export function TextAreaField({ variable }: Props) {
    const { name, label, required } = variable;
    const [field, , helpers] = useField<string>(getFormikFieldValueReference(name));
    return (
        <FormGroup key={`formtext_${name}`} label={label} labelFor="text-input" labelInfo={required ? "*" : undefined}>
            <TextArea
                key={`textarea_${name}`}
                value={field.value}
                fill
                growVertically={true}
                large={true}
                onChange={(e) => helpers.setValue(e.target.value)}
            />
        </FormGroup>
    );
}

export function CheckboxField({ variable }: Props) {
    const { name, label } = variable;
    const [field, , helpers] = useField<boolean>(getFormikFieldValueReference(name));
    return (
        <Checkbox
            key={`checkbox_${name}`}
            inline
            alignIndicator="left"
            label={label}
            checked={field.value}
            onChange={() => helpers.setValue(!field.value)}
        />
    );
}

//todo: figure out timezones
//todo close popover on date select
export function DateField({ variable }: Props) {
    const { name, label, required } = variable;
    const [field, , helpers] = useField<string>(getFormikFieldValueReference(name));

    return (
        <FormGroup
            key={`formstring_${name}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
        >
            <Popover2
                placement="bottom-end"
                interactionKind="click"
                content={
                    <div className="time-shortcuts">
                        <DatePicker
                            highlightCurrentDay
                            className={Classes.ELEVATION_1}
                            minDate={new Date()}
                            onChange={(e) => {
                                helpers.setValue(e.toDateString());
                            }}
                        />
                    </div>
                }
            >
                <Button icon="calendar" text={field.value ? field.value : "Select a Date"} rightIcon="caret-down" />
            </Popover2>
            {/* <InputGroup
                key={`inputstring_${name}`}
                value={field.value}
                onChange={(e) => setFieldValue(field.name, e.target.value)}
            /> */}
        </FormGroup>
    );
}

const ChoiceSelect = Select.ofType<string>();

const filterChoice: ItemPredicate<string> = (query, choice) => {
    return choice.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
const renderChoice: ItemRenderer<string> = (choice, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }
    return <MenuItem active={modifiers.active} key={`choice_${uuid()}`} onClick={handleClick} text={choice} />;
};
export function SelectField({ variable }: Props) {
    const { name, label, choices, required } = variable;
    const [field, , helpers] = useField<string>(getFormikFieldValueReference(name));
    return (
        <FormGroup
            key={`select_${name}`}
            label={label}
            labelFor="text-input"
            labelInfo={required ? "*" : undefined}
            helperText={`Please select a value from the list.`}
        >
            <ChoiceSelect
                itemPredicate={filterChoice}
                itemRenderer={renderChoice}
                items={choices ? choices : []}
                onItemSelect={(c) => {
                    helpers.setValue(c);
                }}
                noResults={<MenuItem disabled={true} text="No results." />}
                popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
            >
                <Button text={field.value ? field.value : "..."} rightIcon="caret-down" />
            </ChoiceSelect>
            {/* //todo: fiugreout how to clear active item */}
            {field.value && <Button icon="cross" minimal intent="danger" onClick={() => helpers.setValue("")} />}
        </FormGroup>
    );
}
